import jsPDF from 'jspdf';
import autoTable, { CellDef, RowInput, Styles } from 'jspdf-autotable';
import _, { each, filter, forEach, reduce } from "lodash";
import { DateTime as LuxonDateTime } from 'luxon';
import numeral from 'numeral';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, DropdownToggle, Nav, Navbar, NavbarBrand, UncontrolledDropdown } from "reactstrap";
import { gql, useQuery } from "urql";
import { WorkBook, WorkSheet } from "xlsx/types";
import { Loading } from ".";
import { DangerousGoods, HacpackQuery, HacpackQueryTerminalArguments, Handling, HandlingType, Terminal, TerminalHandlingsArguments } from "../types/graph";
import { useTeusCount } from "../hooks";
import { GraphDeepPartialNotListed, GraphDeepPartialNotListedAndOverride, GraphDeepPartialOverride } from "../types";
import PdfViewer from './PdfViewer';
import { GridLayout } from "./ui";

type StorageReportQueryData = GraphDeepPartialOverride<HacpackQuery, {
    terminal: StorageReportTerminal
}>
type StorageReportTerminal = GraphDeepPartialNotListedAndOverride<Terminal, 'id' | 'name' | 'slug' | 'port' | 'ceiLimit' | 'centerLatitude' | 'centerLongitude' | 'centerLongitude' | 'mapZoom', {
    handlings: StorageReportTerminalHandling[]
}>
type StorageReportTerminalHandling = GraphDeepPartialNotListedAndOverride<Handling, 'id' | 'loadingUnitIdentifier' | 'checkedIn' | 'checkedOut' | 'type' | 'ship' | 'loadingUnitSizeFromGate' | 'loadingUnitSize', {
    dangerousGoods: StorageReportTerminalHandlingsDangerousGoods[]
}>
type StorageReportTerminalHandlingsDangerousGoods = GraphDeepPartialNotListed<DangerousGoods, 'id' | 'adrClass' | 'weight' | 'numberOfPackages' | 'unNumber'>
type StorageReportQueryArguments = HacpackQueryTerminalArguments & TerminalHandlingsArguments;

const storageReportQuery = gql<StorageReportQueryData, StorageReportQueryArguments>`
query terminal($id: Int!, $types: [HandlingType!]) {
    terminal (id: $id) {
        id
        name
        teusLimit
        handlings (types: $types) {
            id
            loadingUnitIdentifier
            checkedIn
            checkedOut
            type
            ship
            loadingUnitSize
            loadingUnitSizeFromGate
            checkInDate
            dangerousGoods {
                id
                adrClass
                weight
                numberOfPackages
                unNumber
            }
        }
    }
}
`;



type StorageReportProps = {
    id: number
};

//const A = 'A'.charCodeAt(0);

//const indexToCol = (index) => {
//    return String.fromCharCode(index + A);
//};

const StorageReport = ({ id }: StorageReportProps) => {

    const { t } = useTranslation('common');
    //const { terminal } = useContext(TerminalContext);
    const [pdf, setPdf] = useState<jsPDF | null>(null);

    const [result] = useQuery<StorageReportQueryData, StorageReportQueryArguments>({
        query: storageReportQuery,
        variables: { id, types: [HandlingType.None, HandlingType.Boarding, HandlingType.Unboarding, HandlingType.TranshipmentBoarding, HandlingType.TranshipmentUnboarding] }
    });

    const { data } = result;

    const terminal = data?.terminal;

    const handlings = useMemo(() => terminal?.handlings === undefined ? undefined : _(terminal?.handlings).orderBy('loadingUnitIdentifier').value(), [terminal]);
    const teusLimit = terminal?.teusLimit;

    const teusCount = useTeusCount({ handlings, teusLimit: teusLimit ?? undefined });

    //const handlingsCount = useMemo<number>(() => handlings?.length ?? 0, [handlings])
    //const handlingsLastId = useMemo<number>(() => _(handlings).maxBy('checkinDate')?.id ?? 0, [handlings])

    const colNames = useMemo<string[]>(() => [
        t('terminal.data.handlings.columns.loadingUnitIdentifier.name').toString(),
        t('terminal.data.handlings.columns.ship.name').toString(),
        t('terminal.data.handlings.columns.loadingUnitSizeFromGate.name').toString(),
        t('terminal.data.handlings.columns.checkInDate.name').toString(),
        t('terminal.data.dangerousGoods.columns.adrClass.name').toString(),
        t('terminal.data.dangerousGoods.columns.weight.name').toString(),
        t('terminal.data.dangerousGoods.columns.numberOfPackages.name').toString(),
        t('terminal.data.dangerousGoods.columns.unNumber.name').toString()
    ], [t])

    const makePdfCallback = useCallback((resolve:any, reject:any) => {
        if (handlings === undefined) {           
            return;
        }

        const doc = new jsPDF('landscape', 'mm', [297, 210]);

        autoTable(doc, {
            head: [colNames],
            didDrawPage: function (data) {
                // Footer
                if (teusLimit === null) {
                    return;
                }

                const { current, expected, remaining } = teusCount;

                var str = t('Conteggio TEU = {{current}} giacenti, {{expected}} previsti, {{remaining}} residui', {
                    current: numeral(current).format('0[.]0'),
                    expected: numeral(expected).format('0[.]0'),
                    remaining: numeral(remaining).format('0[.]0')
                })
                doc.setFontSize(10)

                // jsPDF 1.4+ uses getWidth, <1.4 uses .width
                const pageSize = doc.internal.pageSize
                const pageHeight = pageSize.getHeight()
                doc.text(str, data.settings.margin.left, pageHeight - 10)
            },
            margin: { top: 30 },
            body: reduce<StorageReportTerminalHandling, RowInput[]>(filter(handlings, 'checkedIn'), (result, handling, key) => {

                const rowSpan = Math.max(1, handling.dangerousGoods?.length ?? 1);
                const styles: Partial<Styles> = { valign: 'top' }

                const dt = LuxonDateTime.fromISO((handling.checkInDate ?? new Date(Date.now()).toISOString()).toString());
                const dg1 = handling.dangerousGoods?.[0] ?? null;
                const checkInDtStr = dt.hour + dt.minute + dt.second + dt.millisecond === 0 ? dt.toLocaleString(LuxonDateTime.DATE_MED) : dt.toLocaleString(LuxonDateTime.DATETIME_MED)

                const row1: CellDef[] = [
                    { content: handling.loadingUnitIdentifier ?? '', rowSpan, styles },
                    { content: handling.ship ?? '', rowSpan, styles },
                    { content: handling.loadingUnitSizeFromGate ?? '', rowSpan, styles },
                    { content: checkInDtStr, rowSpan, styles }
                ];

                if (dg1 === null) {
                    row1.push({ content: 'Dati mancanti', colSpan: 4, styles })
                } else {
                    const w = (dg1.weight ?? null) === null ? '' : `${numeral(dg1.weight).format('0[.]00')} kg`;

                    row1.push({ content: handling.dangerousGoods?.[0]?.adrClass ?? '', styles },
                        { content: w, styles },
                        { content: dg1.numberOfPackages ?? '', styles },
                        { content: dg1.unNumber ?? '', styles })
                }

                result.push(row1);

                _(handling.dangerousGoods).slice(1).forEach((dg) => {
                    const w = (dg.weight ?? null) === null ? '' : `${numeral(dg.weight).format('0[.]00')} kg`;

                    result.push([
                        { content: dg.adrClass ?? '', styles },
                        { content: w, styles },
                        { content: dg.numberOfPackages ?? '', styles },
                        { content: dg.unNumber ?? '', styles }
                    ]);
                });

                return result;
            }, [] as RowInput[])
        })


        resolve(doc);
    }, [handlings, teusCount, teusLimit, colNames,t])

    const makePdf = useCallback(() => {
        return new Promise<jsPDF>(makePdfCallback);
    }, [makePdfCallback]);

    useEffect(() => {
        let unmounted = false;

        makePdf().catch().then((pdf) => {
            if (unmounted) {
                return;
            }
            setPdf(pdf)
        });

        return () => {
            unmounted = true;
        }
    }, [makePdf, setPdf])

    const exportPdf = useCallback(() => makePdf().catch().then((doc) => doc.save('hacpack.pdf')), [makePdf]);

    const exportXls = useCallback(() => {
        import('xlsx').then(({ /*default: XLSX,*/ utils: { encode_cell, encode_range }, writeFile }) => {
            var r = 0;

            const gws: WorkSheet = {
                '!merges': []
            };

            each(colNames, (v, c) => {
                gws[encode_cell({ r, c })] = { t: 's', v }
            });

            r++;

            /* make worksheet */
            each(handlings, ({ loadingUnitIdentifier, ship, loadingUnitSizeFromGate, checkInDate, dangerousGoods }) => {

                gws[encode_cell({ r, c: 0 })] = {
                    t: 's', v: loadingUnitIdentifier, s: {
                        alignment: {
                        }
                    }
                };
                gws[encode_cell({ r, c: 1 })] = { t: 's', v: ship ?? undefined };
                gws[encode_cell({ r, c: 2 })] = { t: 'n', v: loadingUnitSizeFromGate ?? undefined };
                gws[encode_cell({ r, c: 3 })] = { t: 'd', v: checkInDate ?? undefined };

                if ((dangerousGoods ?? [] as DangerousGoods[]).length > 0) {
                    const sr = r;

                    forEach(dangerousGoods, ({ adrClass, weight, numberOfPackages, unNumber }) => {

                        gws[encode_cell({ r, c: 4 })] = { t: 's', v: adrClass ?? undefined };
                        gws[encode_cell({ r, c: 5 })] = { t: 'n', v: weight ?? undefined, z: '0#" kg"' };
                        gws[encode_cell({ r, c: 6 })] = { t: 'n', v: numberOfPackages ?? undefined };
                        gws[encode_cell({ r, c: 7 })] = { t: 's', v: unNumber ?? undefined };

                        r++;
                    });

                    if ((dangerousGoods as DangerousGoods[]).length > 1) {
                        const er = r - 1;

                        gws['!merges']?.push({ s: { r: sr, c: 0 }, e: { r: er, c: 0 } });
                        gws['!merges']?.push({ s: { r: sr, c: 1 }, e: { r: er, c: 1 } });
                        gws['!merges']?.push({ s: { r: sr, c: 2 }, e: { r: er, c: 2 } });
                        gws['!merges']?.push({ s: { r: sr, c: 3 }, e: { r: er, c: 3 } });
                    }

                } else {
                    gws[encode_cell({ r, c: 4 })] = { t: 's', v: 'Dati mancanti' };
                    gws['!merges']?.push({ s: { r, c: 4 }, e: { r, c: 7 } });

                    r++;
                }
            });

            gws['!ref'] = encode_range({ s: { r: 0, c: 0 }, e: { r: r - 1, c: 7 } });

            const sheetNames = [t('Giacenze')];
            const sheets:Record<string, WorkSheet> = {};

            sheets[t('Giacenze')] = gws;

            if (teusLimit !== null) {
                const tws: WorkSheet = { '!merges': [] };

                tws['!ref'] = encode_range({ s: { r: 0, c: 0 }, e: { r: 1, c: 2 } });

                const { current, remaining, expected } = teusCount;

                tws[encode_cell({ r: 0, c: 0 })] = { t: 's', v: t('Giacenti') };
                tws[encode_cell({ r: 1, c: 0 })] = { t: 'n', v: current };
                tws[encode_cell({ r: 0, c: 1 })] = { t: 's', v: t('Previsti') };
                tws[encode_cell({ r: 1, c: 1 })] = { t: 'n', v: remaining };
                tws[encode_cell({ r: 0, c: 2 })] = { t: 's', v: t('Residui') };
                tws[encode_cell({ r: 1, c: 2 })] = { t: 'n', v: expected };

                sheetNames.push(t('Conteggio TEU'));
                sheets[t('Conteggio TEU')] = tws;
            }

            writeFile({
                SheetNames: sheetNames,
                Sheets: sheets
            } as WorkBook, 'hacpack.xlsx');
        });
    }, [colNames, handlings, teusCount, teusLimit, t]);

    if (handlings === null) {
        return <Loading />
    }

    const header = (
        <header>
            <Navbar className="navbar ng-white border-bottom box-shadow" style={{ paddingBottom: 0, justifyContent: 'left' }} light>
                <NavbarBrand>
                    {t(`terminal.storageReport.title`)}
                </NavbarBrand>
                <Nav navbar>
                    <UncontrolledDropdown nav>
                        <DropdownToggle nav caret>
                            Esporta
                        </DropdownToggle>
                        <DropdownMenu right>
                            <DropdownItem onClick={exportXls}>XLS</DropdownItem>
                            <DropdownItem onClick={exportPdf}>PDF</DropdownItem>
                        </DropdownMenu>
                    </UncontrolledDropdown>
                </Nav>
            </Navbar>
        </header>);

    const content = pdf === null ? <Loading /> : <PdfViewer url={pdf.output('datauristring')} />;

    return (
        <GridLayout header={header}>{content}</GridLayout>
    );
}

export default StorageReport;